'use client';

import { useState, Suspense, useEffect } from 'react';
import { MOTION, LIFT } from '@/shared/components/ui/motion';
import { BadgeSuccessIcon, HouseOutlinedIcon } from '@/shared/components/icons';
import { cn } from '@/shadcn/lib/utils';
import { SearchIcon, EuroIcon } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useSearchParams } from 'next/navigation';
import { LISTING_PROPERTY_SUBTYPE_LABELS } from '@/features/listings/definitions/propertyTypes';
import { AdvertsClassicAdvancedFilter } from './AdvertsClassicAdvancedFilter';
import { MultiSelectPopover } from '@/shared/components/ui/MultiSelectPopover';
import { LocationOutlinedIcon, FilterIcon } from '@/shared/components/icons';
import { Label } from '@/shadcn/components/ui/label';
import { useListingsClassicFilter } from '../../listings/hooks/useListingsClassicFilter';
import { ListingsFilterSettings } from '../../listings/types';
import {
    PRICE_STEP,
    PRICE_FALLBACK_MAX,
    PRICE_QUICK_RANGES,
    formatPrice,
} from '../constants/filterConstants';
import { TxnTypeOptions } from './fields/TxnTypeOptions';
import { PropertyTypeAccordion } from './fields/PropertyTypeAccordion';
import { RangeField } from './fields/RangeField';
import { RX_CONTROL_SQ } from '@/shared/components/ui/controls';
import { LocationOptions } from '@/features/locations/components/LocationOptions';

interface Props {
    filterSettings: ListingsFilterSettings;
    showUseSmartFilter: boolean;
    onSetSmartFilter: () => void;
    /** Vlozeny rezim v glass hero karte - bez vlastnej bielej karty a bez search buttonu.
        Styl poli sa este vymysli (biele nebudu). */
    embedded?: boolean;
    /** Hero jednoriadkovy rezim: len okruhly Filtre button (otvara rozsireny
        filter), ziadne polia. Sedi v riadku vedla smart inputu. */
    compact?: boolean;
}

/** pod xl (1280) su embedded fieldy uzke - kratke placeholdery, aby sa nic nezalamovalo */
function useShortLabels(enabled: boolean) {
    const [short, setShort] = useState(false);
    useEffect(() => {
        if (!enabled) return;
        const mq = window.matchMedia('(max-width: 1279px)');
        const apply = () => setShort(mq.matches);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, [enabled]);
    return enabled && short;
}

export function AdvertsClassicFilter(props: Props) {
    const shortLabels = useShortLabels(props.embedded === true);
    const [searchParams, setSearchParams] = useState(new URLSearchParams({}));
    const [advancedOpen, setAdvancedOpen] = useState(false);

    const filterState = useListingsClassicFilter(props.filterSettings, searchParams);
    const { formik, displayLocations, startedSearchLocations, startSearchLocation } = filterState;

    //  console.log('values', formik.values);

    // console.log('filter state', filterState);

    if (props.compact) {
        return (
            <>
                <Suspense>
                    <GetSearchParams onSearchParams={(params) => setSearchParams(params)} />
                </Suspense>
                <a
                    href="#"
                    aria-label="Rozšírené filtre"
                    title="Filtre"
                    className={cn(
                        'flex justify-center items-center rounded-full cursor-pointer shrink-0',
                        'size-[40px] min-w-[40px] md:size-[45px] md:min-w-[45px] xl:size-[52px] xl:min-w-[52px]',
                        'bg-[rgba(255,255,255,.5)] text-black/80 border border-transparent',
                        'duration-[260ms] hover:bg-white hover:text-black',
                        'hover:shadow-[0_11px_27px_0_rgba(0,0,0,0.14)]',
                        LIFT,
                        MOTION,
                    )}
                    onClick={(e) => {
                        e.preventDefault();
                        setAdvancedOpen(true);
                    }}
                >
                    <FilterIcon className="size-[17px] xl:size-[19px]" />
                </a>
                <AdvertsClassicAdvancedFilter
                    open={advancedOpen}
                    onClose={() => setAdvancedOpen(false)}
                    filterState={filterState}
                    filterSettings={props.filterSettings}
                />
            </>
        );
    }

    return (
        <>
            <Suspense>
                <GetSearchParams onSearchParams={(params) => setSearchParams(params)} />
            </Suspense>

            <div className="w-full">
                <div
                    className={cn(
                        props.embedded
                            ? 'mt-[11px] md:mt-[20px] xl:mt-[25px]'
                            : cn(
                                  'mt-[10px] bg-[#fff] py-[20px] px-[15px] rounded-[18px] shadow-[0_11px_30px_0_rgba(0,0,0,0.15)]',
                                  'lg:p-[25px] xl:p-[35px] xl:rounded-[25px]',
                              ),
                    )}
                >
                    <div
                        className={cn(
                            props.embedded
                                ? 'flex items-center gap-[10px] md:gap-[12px] xl:gap-[15px]'
                                : 'flex items-start gap-[0px]',
                        )}
                    >
                        <div
                            className={cn(
                                'min-w-0 flex-1 flex sm:grid sm:grid-cols-[60fr_40fr]',
                                props.embedded
                                    ? 'md:grid-cols-[34fr_20fr_28fr_18fr] gap-[8px] xl:gap-[12px]'
                                    : 'md:grid-cols-[45fr_20fr_35fr] xl:gap-[2px]',
                            )}
                        >
                            {/* Location */}
                            <div className="relative min-w-0 w-full">
                                <MultiSelectPopover
                                    bare={props.embedded}
                                    type="multi"
                                    label="Kde?"
                                    placeholder={shortLabels ? 'Miesto' : 'Vyberte miesto'}
                                    items={formik.values.locations.map((loc) => ({
                                        id: loc.slug,
                                        value: loc.name,
                                    }))}
                                    iconLeft={
                                        <LocationOutlinedIcon className="size-[16px] text-black/80" />
                                    }
                                    onChangeQuery={(value) => startSearchLocation(value)}
                                    onDeleteItem={(item) => {
                                        formik.setFieldValue(
                                            'locations',
                                            formik.values.locations.filter(
                                                (i) => i.slug !== item.id,
                                            ),
                                        );
                                    }}
                                >
                                    <LocationOptions
                                        value={formik.values.locations}
                                        onChange={(next) =>
                                            formik.setFieldValue('locations', next)
                                        }
                                        displayLocations={displayLocations}
                                        startedSearchLocations={startedSearchLocations}
                                    />
                                </MultiSelectPopover>
                                {(formik.submitCount > 0 || formik.touched.locations) &&
                                    formik.errors.locations && (
                                        <div className="ml-[5px] absolute -bottom-[15px]">
                                            <p className="text-[11px] text-red-500">
                                                {formik.errors.locations as string}
                                            </p>
                                        </div>
                                    )}
                            </div>

                            {/* Transaction type */}
                            <div className="relative min-w-0">
                                <MultiSelectPopover
                                    bare={props.embedded}
                                    type="classic"
                                    label="Typ inzerátu:"
                                    placeholder={shortLabels ? 'Typ' : 'Zvoľte typ inzerátu'}
                                    items={
                                        formik.values.txnType
                                            ? [
                                                  {
                                                      id: formik.values.txnType.slug,
                                                      value: formik.values.txnType.value,
                                                  },
                                              ]
                                            : []
                                    }
                                    iconLeft={
                                        <BadgeSuccessIcon className="size-[18px] text-black/80" />
                                    }
                                    className="hidden sm:flex"
                                    menuClassName="sm:min-w-[240px]"
                                >
                                    <TxnTypeOptions
                                        value={formik.values.txnType}
                                        onChange={(type) =>
                                            formik.setFieldValue('txnType', type)
                                        }
                                    />
                                </MultiSelectPopover>
                                {(formik.submitCount > 0 || formik.touched.txnType) &&
                                    formik.errors.txnType && (
                                        <div className="ml-[5px] absolute -bottom-[15px]">
                                            <p className="text-[11px] text-red-500">
                                                {formik.errors.txnType as string}
                                            </p>
                                        </div>
                                    )}
                            </div>

                            {/* Property type */}
                            <div className="relative min-w-0">
                                <MultiSelectPopover
                                    bare={props.embedded}
                                    type="multi"
                                    label="Nehnuteľnosť:"
                                    placeholder={shortLabels ? 'Nehnuteľnosť' : 'Vyberte nehnuteľnosť'}
                                    items={formik.values.propertyTypes
                                        .filter((t): t is Exclude<typeof t, null> => t !== null)
                                        .map((type) => ({
                                            id: type,
                                            value: LISTING_PROPERTY_SUBTYPE_LABELS[type],
                                        }))}
                                    iconLeft={
                                        <HouseOutlinedIcon className="size-[15px] text-black/80" />
                                    }
                                    className="hidden md:flex"
                                    menuClassName="md:min-w-[360px] md:left-auto md:right-0"
                                    onDeleteItem={(item) => {
                                        formik.setFieldValue(
                                            'propertyTypes',
                                            formik.values.propertyTypes.filter(
                                                (i) => i !== item.id,
                                            ),
                                        );
                                    }}
                                >
                                    <PropertyTypeAccordion
                                        value={formik.values.propertyTypes}
                                        onChange={(next) =>
                                            formik.setFieldValue('propertyTypes', next)
                                        }
                                    />
                                </MultiSelectPopover>
                            </div>

                            {/* Cena - len hero embedded; dvojuchytovy slider od-do */}
                            {props.embedded &&
                                (() => {
                                    const priceMin = props.filterSettings.minPrice ?? 0;
                                    const priceMax =
                                        props.filterSettings.maxPrice ?? PRICE_FALLBACK_MAX;
                                    const priceFrom = filterState.priceFrom ?? priceMin;
                                    const priceTo = filterState.priceTo ?? priceMax;
                                    const atFrom = priceFrom <= priceMin;
                                    const atTo = priceTo >= priceMax;
                                    const summary = atFrom
                                        ? atTo
                                            ? ''
                                            : 'do ' + formatPrice(priceTo)
                                        : atTo
                                          ? 'od ' + formatPrice(priceFrom)
                                          : formatPrice(priceFrom) + ' – ' + formatPrice(priceTo);
                                    return (
                                        <div className="relative min-w-0 hidden md:block">
                                            <MultiSelectPopover
                                                bare
                                                type="classic"
                                                placeholder="Cena"
                                                items={
                                                    summary
                                                        ? [{ id: 'price', value: summary }]
                                                        : []
                                                }
                                                iconLeft={<EuroIcon className="size-[15px]" />}
                                                className="hidden md:flex"
                                                menuClassName="md:min-w-[400px] md:left-auto md:right-0"
                                                keepOpenOnItemsChange
                                            >
                                                <div className="w-full pt-[6px] pb-[4px]">
                                                    <RangeField
                                                        min={priceMin}
                                                        max={priceMax}
                                                        step={PRICE_STEP}
                                                        from={priceFrom}
                                                        to={priceTo}
                                                        format={formatPrice}
                                                        quickRanges={PRICE_QUICK_RANGES}
                                                        labelFrom="Cena od"
                                                        labelTo="Cena do"
                                                        onChange={(from, to) => {
                                                            filterState.setPriceFrom(from);
                                                            filterState.setPriceTo(to);
                                                        }}
                                                    />
                                                </div>
                                            </MultiSelectPopover>
                                        </div>
                                    );
                                })()}
                        </div>

                        {/* Actions */}
                        <div
                            className={cn(
                                'min-w-0 flex gap-[10px] xl:gap-[12px]',
                                props.embedded ? 'items-center' : 'items-end xl:pl-[7px]',
                            )}
                        >
                            <div>
                                {!props.embedded && (
                                    <Label className="text-[13px] font-[600] mb-[8px] xl:text-[14px]">
                                        Filtre:
                                    </Label>
                                )}
                                <a
                                    href="#"
                                    aria-label="Rozšírené filtre"
                                    title="Rozšírené filtre"
                                    className={cn(
                                        'flex justify-center items-center rounded-full cursor-pointer',
                                        // embedded: rovnaka vyska ako nav pilly a bare fieldy (RX_CONTROL_H token)
                                        props.embedded
                                            ? RX_CONTROL_SQ
                                            : 'size-[40px] min-w-[40px] xl:size-[45px] xl:min-w-[45px]',
                                        props.embedded
                                            ? cn(
                                                  'bg-[#f8e5b0] text-black/80 border border-transparent',
                                                  'duration-[260ms] hover:bg-[#fbeec8] hover:text-black',
                                              )
                                            : 'border hover:bg-black/[.05] hover:border-black/30',
                                        LIFT,
                                        MOTION,
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setAdvancedOpen(true);
                                    }}
                                >
                                    <FilterIcon className="size-[17px]" />
                                </a>
                            </div>
                            {!props.embedded && (
                                <Button
                                    color="black"
                                    className="px-[12px] size-[40px] lg:size-[auto]"
                                    onClick={() => formik.submitForm()}
                                >
                                    <SearchIcon className="size-[18px] text-[#fff]" />
                                    <p className="hidden lg:block text-[13px]">Vyhľadať</p>
                                </Button>
                            )}
                        </div>
                    </div>

                    {props.showUseSmartFilter && (
                        <div className="flex justify-center text-center mt-[20px]">
                            <a
                                href="#"
                                className="text-[10px] leading-[120%] text-[#aaa] md:text-[11px] xl:text-[12px]"
                                onClick={() => props.onSetSmartFilter()}
                            >
                                Použiť smart filter
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <AdvertsClassicAdvancedFilter
                open={advancedOpen}
                onClose={() => setAdvancedOpen(false)}
                filterState={filterState}
                filterSettings={props.filterSettings}
            />
        </>
    );
}

function GetSearchParams({
    onSearchParams,
}: {
    onSearchParams: (params: URLSearchParams) => void;
}) {
    const searchParams = useSearchParams();
    useEffect(() => {
        if (searchParams) {
            onSearchParams(searchParams);
        }
    }, [searchParams]);
    return <></>;
}
